// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Configuracoes/IbptTab.tsx
//
//  Aba "IBPT" de Configurações Fiscais: tabela "De Olho no Imposto" (Lei
//  12.741/2012). É dela que sai o valor aproximado dos tributos informado na
//  NF-e de venda a consumidor final. Carrega o arquivo oficial da UF e permite
//  informar as alíquotas de um NCM à mão.
// ════════════════════════════════════════════════════════════════════════════

import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info, Save, Upload } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { apiClient, getApiErrorMessage } from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

interface IbptTabela { uf: string | null; versao: string | null; ncms: number; vigenciaFim: string | null }
interface IbptSituacao { carregada: boolean; tabelas: IbptTabela[] }
interface IbptImportado { uf: string; versao: string | null; ncms: number; servicos: number; vigenciaFim: string | null }

const CHAVE = ['config-ibpt'];
const fmtData = (v: string | null) => (v ? new Date(v).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—');
const vencida = (v: string | null) => !!v && new Date(v).getTime() < new Date().setHours(0, 0, 0, 0);

const ncmVazio = { ncm: '', nacional: '', importado: '', estadual: '', municipal: '', uf: '' };
const numero = (v: string) => Number(v.replace(',', '.')) || 0;

export default function IbptTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: CHAVE,
    queryFn: async () => (await apiClient.get<IbptSituacao>('/api/config/ibpt')).data,
  });

  const arquivoRef = useRef<HTMLInputElement>(null);
  const [uf, setUf] = useState('');
  const [importando, setImportando] = useState(false);
  const [ncm, setNcm] = useState(ncmVazio);
  const [salvando, setSalvando] = useState(false);

  const importar = async () => {
    const arquivo = arquivoRef.current?.files?.[0];
    if (!arquivo) { toast.error('Escolha o arquivo CSV do IBPT.'); return; }
    setImportando(true);
    try {
      const form = new FormData();
      form.append('arquivo', arquivo);
      const { data: r } = await apiClient.post<IbptImportado>(
        '/api/config/ibpt/importar', form,
        { headers: { 'Content-Type': 'multipart/form-data' }, params: uf ? { uf } : undefined, timeout: 120_000 },
      );
      toast.success(`Tabela ${r.uf}${r.versao ? ` versão ${r.versao}` : ''} carregada: ${r.ncms.toLocaleString('pt-BR')} NCMs.`);
      if (arquivoRef.current) arquivoRef.current.value = '';
      await queryClient.invalidateQueries({ queryKey: CHAVE });
    } catch (err) {
      toast.error('Não foi possível carregar a tabela.', { description: getApiErrorMessage(err) });
    } finally {
      setImportando(false);
    }
  };

  const salvarNcm = async () => {
    setSalvando(true);
    try {
      await apiClient.put('/api/config/ibpt/ncm', {
        ncm: ncm.ncm, nacional: numero(ncm.nacional), importado: numero(ncm.importado),
        estadual: numero(ncm.estadual), municipal: numero(ncm.municipal), uf: ncm.uf || null,
      });
      toast.success(`Alíquotas do NCM ${ncm.ncm} gravadas.`);
      setNcm(ncmVazio);
      await queryClient.invalidateQueries({ queryKey: CHAVE });
    } catch (err) {
      toast.error('Não foi possível gravar o NCM.', { description: getApiErrorMessage(err) });
    } finally {
      setSalvando(false);
    }
  };

  const campo = (chave: keyof typeof ncmVazio, rotulo: string) => (
    <div className="space-y-1">
      <Label>{rotulo}</Label>
      <Input inputMode="decimal" placeholder="0,00" value={ncm[chave]}
        onChange={(e) => setNcm((n) => ({ ...n, [chave]: e.target.value }))} />
    </div>
  );

  return (
    <div className="space-y-4 pt-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Para que serve</AlertTitle>
        <AlertDescription>
          Na venda a consumidor final, a NF-e informa o valor aproximado dos tributos (Lei 12.741/2012), calculado
          pelas alíquotas desta tabela para o NCM de cada item. NCM sem alíquota aqui sai sem o valor.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tabelas carregadas</CardTitle>
          <CardDescription>Uma tabela por UF; carregar de novo substitui a tabela daquela UF.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-16 w-full" /> : !data?.carregada ? (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" /> Nenhuma tabela carregada: as notas saem sem o valor aproximado dos tributos.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>UF</TableHead><TableHead>Versão</TableHead>
                  <TableHead className="text-right">NCMs</TableHead><TableHead>Válida até</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.tabelas.map((t) => (
                  <TableRow key={`${t.uf}-${t.versao}`}>
                    <TableCell>{t.uf ?? 'Sem UF (informado à mão)'}</TableCell>
                    <TableCell>{t.versao ?? '—'}</TableCell>
                    <TableCell className="text-right">{t.ncms.toLocaleString('pt-BR')}</TableCell>
                    <TableCell>
                      {fmtData(t.vigenciaFim)}
                      {vencida(t.vigenciaFim) && <Badge variant="destructive" className="ml-2">Vencida</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Carregar o arquivo do IBPT</CardTitle>
          <CardDescription>
            Arquivo CSV oficial da UF (TabelaIBPTax&lt;UF&gt;&lt;versão&gt;.csv). A UF é lida do nome do arquivo; informe-a se o arquivo foi renomeado.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1 flex-1 min-w-[240px]">
            <Label>Arquivo CSV</Label>
            <Input ref={arquivoRef} type="file" accept=".csv,text/csv" />
          </div>
          <div className="space-y-1 w-24">
            <Label>UF</Label>
            <Input value={uf} maxLength={2} placeholder="MG" onChange={(e) => setUf(e.target.value.toUpperCase())} />
          </div>
          <Button onClick={importar} disabled={importando} className="gap-2">
            <Upload className="h-4 w-4" /> {importando ? 'Carregando…' : 'Carregar tabela'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Informar um NCM à mão</CardTitle>
          <CardDescription>
            Alíquotas em % para um NCM, sem depender do arquivo. Com a UF em branco vale para qualquer UF que não tenha a linha na tabela carregada.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end">
          <div className="space-y-1 md:col-span-2">
            <Label>NCM (8 dígitos)</Label>
            <Input value={ncm.ncm} maxLength={8} inputMode="numeric" placeholder="19052090"
              onChange={(e) => setNcm((n) => ({ ...n, ncm: e.target.value.replace(/\D/g, '') }))} />
          </div>
          {campo('nacional', 'Federal nacional %')}
          {campo('importado', 'Federal importado %')}
          {campo('estadual', 'Estadual %')}
          {campo('municipal', 'Municipal %')}
          <div className="space-y-1">
            <Label>UF</Label>
            <Input value={ncm.uf} maxLength={2} placeholder="Todas"
              onChange={(e) => setNcm((n) => ({ ...n, uf: e.target.value.toUpperCase() }))} />
          </div>
          <div className="col-span-2 md:col-span-7 flex justify-end">
            <Button onClick={salvarNcm} disabled={salvando || ncm.ncm.length !== 8} className="gap-2">
              <Save className="h-4 w-4" /> {salvando ? 'Gravando…' : 'Gravar NCM'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
